import React, { useMemo, useState } from 'react';
import { Loader, Textarea } from '@mantine/core';
import { Button } from '../../../styledComponents';
import UsageRightImageContainer from './UsageRightImageContainer';
import { UsageRightRequest } from '../types';
import CustomSelect from '../../../common/CustomSelect/CustomSelect';
import styles from '../../../../styles/usageRights.module.css';
import TermDropDown from './TermDropDown';
import { useApi } from '../../../../hooks/useApi';
import { reviseUsageRights } from '../../../../services/usageRights.service';
import { useUsageRight } from '../UsageRightsProvider';
import { getCurrencyLogo } from '../../../../utils/global';

interface TermSelectLabelProps {
  value: string;
}

function TermSelectLabel({ value }: TermSelectLabelProps) {
  return <div className={styles.usageRightRevisionTermSelectLabel}>{value}</div>;
}

interface UsageRightRevisionProps {
  request: UsageRightRequest;
  onClose: () => unknown;
}

const DurationOptions = [
  {
    label: '30 days',
    value: 30,
  },
  {
    label: '60 days',
    value: 60,
  },
  {
    label: '90 days',
    value: 90,
  },
  {
    label: 'Forever',
    value: -1,
  },
];

const PaymentOptions = [
  {
    label: '50',
    value: 50,
  },
  {
    label: '100',
    value: 100,
  },
  {
    label: '150',
    value: 150,
  },
  {
    label: '200',
    value: 200,
  },
];

const defaultMessage = 'Hey {{first_name}}, I read the terms but I have a new proposal for the same.';

function UsageRightRevision({ request, onClose }: UsageRightRevisionProps) {
  const [selectedDuration, setSelectedDuration] = useState<number>(() => request.duration);
  const [selectedPayment, setSelectedPayment] = useState<number>(() => {
    let price = 0;
    const ugcFreeProducts = request?.usageRightHistory?.[0]?.ugcFreeProducts;
    if (ugcFreeProducts?.length) {
      ugcFreeProducts.forEach((product) => {
        price += parseFloat(product.productDetails.productPrice) * product.quantity;
      });
    }

    return request.payment || price;
  });
  const [brandMessage, setBrandMessage] = useState(() => defaultMessage.replaceAll('{{first_name}}', request.userName));
  const { clearAndRefetch } = useUsageRight();

  const isDisabled = useMemo(() => {
    if (!selectedDuration) return true;
    if (!selectedPayment) return true;
    if (brandMessage.length === 0) return true;

    return false;
  }, [selectedDuration, selectedPayment, brandMessage]);

  const { triggerFetch, isLoading } = useApi(reviseUsageRights, {
    type: 'mutation',
    onSuccess: () => {
      clearAndRefetch();
      onClose();
    },
  });

  const onSubmit = () => {
    triggerFetch({
      args: {
        usageRightRequestId: request._id,
        duration: selectedDuration,
        payment: selectedPayment,
        message: brandMessage,
      },
    });
  };

  return (
    <div style={{ display: 'flex' }}>
      <div className={styles.usageRightRevisionLeftContainer}>
        <div className={`${styles.usageRightRevisionLeftItemContainer} ${styles.usageRightRevisionLeftContainerTitle}`}>
          Send Revised Usage Terms
        </div>
        <div className={styles.usageRightRevisionPurposeContainer}>
          <div className={styles.usageRightRevisionPurposeContainerTitle}>📝 Purpose</div>
          <div className={styles.usageRightRevisionPurposeContainerValue}>{request.purpose}</div>
        </div>
        <div className={styles.usageRightRevisionLeftItemContainer}>
          <div className={styles.usageRightRevisionTermLabel}>
            The revised duration for which you want to give rights
          </div>
          <div className={styles.usageRightRevisionTermContainer}>
            <div className={styles.usageRightRevisionTermContainerTitle}>📅 Duration</div>
            <CustomSelect
              style={{ minWidth: '257px', padding: '8px 6px 6px 10px' }}
              selectLabel={<TermSelectLabel value={selectedDuration === -1 ? 'Forever' : `${selectedDuration} days`} />}
              dropDownElement={(setDropdownOpen, width) => (
                <TermDropDown
                  inputPlaceholder="Enter in days"
                  options={DurationOptions}
                  value={selectedDuration}
                  setValue={setSelectedDuration}
                  setDropdownOpen={setDropdownOpen}
                  width={width}
                />
              )}
            />
          </div>
        </div>
        <div className={styles.usageRightRevisionLeftItemContainer}>
          <div className={styles.usageRightRevisionTermLabel}>
            The revised duration for which you want to give rights
          </div>
          <div className={styles.usageRightRevisionTermContainer}>
            <div className={styles.usageRightRevisionTermContainerTitle}>💰 Payment</div>
            <CustomSelect
              style={{ minWidth: '257px', padding: '8px 6px 6px 10px' }}
              selectLabel={<TermSelectLabel value={`${getCurrencyLogo(request.defaultCurrency)}${selectedPayment}`} />}
              dropDownElement={(setDropdownOpen, width) => (
                <TermDropDown
                  inputPlaceholder="Custom Amount"
                  options={PaymentOptions}
                  value={selectedPayment}
                  setValue={setSelectedPayment}
                  setDropdownOpen={setDropdownOpen}
                  width={width}
                />
              )}
            />
          </div>
        </div>
        <div className={styles.usageRightRevisionLeftItemContainer}>
          <div className={styles.usageRightRevisionMessageLabel}>💌 Add a message for the brand</div>
          <Textarea
            value={brandMessage}
            onChange={(e) => setBrandMessage(e.target.value)}
            minRows={4}
            classNames={{ input: styles.usageRightTextArea }}
          />
        </div>
        <div className={styles.usageRightRevisionButtonContainer}>
          <Button disabled={isDisabled} bgColor="var(--purple-p-400)" onClick={onSubmit} width="100%">
            {isLoading && <Loader color="white" size="xs" />}
            Send Revised Terms
          </Button>
        </div>
      </div>
      <div className={styles.usageRightRevisionRightContainer}>
        <div style={{ marginBottom: 'auto' }}>
          <UsageRightImageContainer request={request} />
        </div>
        <Button disabled={isDisabled} bgColor="var(--purple-p-400)" onClick={onSubmit}>
          {isLoading && <Loader color="white" size="xs" />}
          Send Revised Terms
        </Button>
      </div>
    </div>
  );
}

export default UsageRightRevision;
